import { APIReferenceType, ArdoqId } from '@ardoq/api-types';
import {
  type GraphEdgeBase,
  GraphInterface,
  LegendReferenceType,
  ParentChildGraphEdge,
  reduceLegendReferenceTypes,
} from '@ardoq/graph';
import { GlobalReferenceTypeId } from '@ardoq/data-model';
import { PARENT_CHILD_REFERENCE } from '@ardoq/global-consts';
import { maybeGrayOutTypes } from 'models/utils/filterUtils';

interface LinksToReferenceTypesReducerState {
  referenceModelTypes: Map<GlobalReferenceTypeId, APIReferenceType>;
  referenceTypes: Map<ArdoqId, APIReferenceType>;
}

const PARENT_CHILD_REFERENCE_LEGEND_REFERENCE_TYPE: LegendReferenceType = {
  ...ParentChildGraphEdge.getType(),
  className: '',
  globalTypeIds: [PARENT_CHILD_REFERENCE],
};

const getLegendReferenceTypes = (
  referenceModelTypes: Map<string, APIReferenceType>,
  hasParentChildLinks: boolean
): LegendReferenceType[] => [
  ...[...referenceModelTypes.entries()]
    .map(([globalTypeId, referenceType]) => ({
      ...referenceType,
      globalTypeId,
      className: '',
    }))
    .reduce(reduceLegendReferenceTypes, {
      result: [],
      keyedResults: new Map(),
      considerLineCaps: true,
    }).result,
  ...(hasParentChildLinks
    ? [PARENT_CHILD_REFERENCE_LEGEND_REFERENCE_TYPE]
    : []),
];

const graphEdgesToReferenceTypes = (
  viewInterface: GraphInterface,
  links: GraphEdgeBase[],
  shouldApplyGreyToReferences: boolean
) => {
  const { referenceModelTypes, referenceTypes } = links.reduce(
    (state: LinksToReferenceTypesReducerState, graphEdge) => {
      const modelId = graphEdge.modelId;
      const referenceTypeId = viewInterface.getGlobalReferenceTypeId(modelId);
      if (!referenceTypeId) {
        return state;
      }
      const knownReferenceType = state.referenceModelTypes.get(referenceTypeId);
      const referenceType =
        knownReferenceType ??
        viewInterface.getReferenceModelType(graphEdge.modelId);
      if (!referenceType) {
        return state;
      }
      if (!knownReferenceType) {
        state.referenceModelTypes.set(referenceTypeId, referenceType);
      }
      state.referenceTypes.set(modelId, referenceType);
      return state;
    },
    { referenceModelTypes: new Map(), referenceTypes: new Map() }
  );
  const hasParentChildLinks = links.some(
    graphEdge => !referenceTypes.get(graphEdge.modelId)?.line
  );

  const legendReferenceTypesBase = getLegendReferenceTypes(
    referenceModelTypes,
    hasParentChildLinks
  );

  return {
    referenceModelTypes,
    referenceTypes,
    legendReferenceTypes: maybeGrayOutTypes(
      legendReferenceTypesBase,
      shouldApplyGreyToReferences
    ),
  };
};

export { graphEdgesToReferenceTypes };
